<template>
  <div ref="vueRemove">
    <!-- Modal testingModal -->
    <div class="modal fade text-black" ref="testingModal" :id="$id('testingModal')" data-backdrop="static" data-keyboard="false" tabindex="-1" :aria-labelledby="$id('payLabel')" aria-hidden="true">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h4 class="modal-title heading" :id="$id('payLabel')">{{ location.name }} Payment</h4>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body">
            <div v-if="timeLeftToPay && timeLeftToPay.asMinutes() < 60" class="mb-2">
              Please complete your payment in the next<br>
              <strong class="text-monospace">{{ timeLeftToPayFormatted }}</strong>
            </div>
            <div v-if="timeExpired" class="mb-2 text-red font-weight-bold">
              Sorry you did not complete your payment in time.
            </div>

            <div class="mb-2">
              Amount to pay: <strong v-if="sale">{{ amountToPay | money }}</strong>
            </div>

            <div class="form-group">
              <input
                v-model="cardholderName"
                :id="$id('cardholder-name')"
                :class="['form-control', nameError ? 'is-invalid' : '']"
                type="text"
                placeholder="Card holder name"
                autocomplete="cc-name"
                :disabled="cardButtonDisable"
                @input="updateName">
              <div v-if="nameError" class="invalid-feedback" role="alert">{{ nameError }}</div>
            </div>

            <div class="form-group">

              <div class="custom-control custom-switch custom-switch-adaptive mb-2"
                :class="[cardError ? 'is-invalid' : '']">
                <input :id="$id('card-pass')" type="checkbox" class="custom-control-input" v-model="cardPass">
                <label :for="$id('card-pass')" class="custom-control-label">{{ cardPass ? 'Card Pass' : 'Card Fail' }}</label>
              </div>

              <div v-if="cardError" class="invalid-feedback" role="alert">{{ cardError }}</div>
            </div>
          </div>

          <div class="modal-footer">
            <button
              type="submit"
              class="btn btn-primary btn-lg btn-block"
              :disabled="cardButtonDisable"
              @click="submitPayment">
              <i class="fas fa-credit-card" aria-hidden="true"></i> Submit Payment
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- .modal testingModal -->

    <!-- Success Modal -->
    <div
      class="modal fade text-black"
      ref="successModal"
      :id="$id('successModal')"
      tabindex="-1"
      :aria-labelledby="$id('successLabel')"
      aria-hidden="true"
      data-backdrop="static"
      data-keyboard="false">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h4 class="modal-title font-weight-bold" :id="$id('successLabel')">{{ location.name }} Payment</h4>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p class="text-black" style="color: black !important">{{ successMessage }}</p>
          </div>
          <div class="modal-footer">
            <p class="text-black" style="color: black !important">Page will reload in {{ reloadTime }}</p>
          </div>
        </div>
      </div>
    </div>
    <!-- Success Modal end -->
  </div>
</template>

<script>
export default {
  props: {
    location: {
      type: Object,
      required: true,
    },

    paymentProviderPublicOptions: {
      type: [Array, Object],
      default: null,
    },
  },

  data() {
    return {
      sale: null,
      saleable: null,
      payingDeposit: false,

      cardholderName: '',
      cardPass: true,
      cardButtonDisable: true,

      amount: null,
      intentId: null,
      paymentExpiry: null,

      isLoading: true,
      loader: null,
      cardError: null,
      nameError: null,
      successMessage: 'Payment successful.',
      reloadTime: 2,

      currentTimeInterval: null,
      currentTime: moment(),

      vouchersToRedeem: [],
    };
  },

  computed: {
    totalOfVoucherRedemption() {
      if (this.vouchersToSpend.length) {
        return parseInt(this.vouchersToSpend.reduce((partialSum, v) => partialSum + v.cashValue, 0));
      }
      return 0;
    },

    amountToPay() {
      if (this.vouchersToRedeem.length) {
        return parseInt(this.amount) - parseInt(this.totalOfVoucherRedemption);
      }

      if (this.amount) {
        return this.amount;
      }

      if (this.sale == null) {
        return 0;
      }

      if (this.payingDeposit) {
        if (this.sale.deposit_amount) {
          return this.sale.deposit_amount;
        }

        return this.sale.sale_items.filter(si => si.saleable_type == 'App\\Models\\Booking').length * 5000;
      }

      return this.sale.left_to_pay;
    },

    timeLeftToPay() {
      if (this.paymentExpiry == null) {
        return null;
      }

      let duration = moment.duration(moment(this.paymentExpiry).subtract(60, 'seconds').diff(this.currentTime));

      return duration;
    },

    timeLeftToPayFormatted() {
      if (this.timeLeftToPay == null) {
        return null;
      }
      if (this.timeLeftToPay < 0) {
        return "00 minutes 00 seconds";
      }

      return this.timeLeftToPay.format('mm [minutes] ss [seconds]', {
        trim: false
      });
    },

    timeExpired() {
      if (this.timeLeftToPay) {
        return this.timeLeftToPay < 0;
      }

      return false;
    },
  },

  watch: {
    sale(newSale, oldSale) {
      if (newSale) {
        this.createIntent();
      } else {
        this.amount = null;
        this.intentId = null;
        this.paymentExpiry = null;
        this.cardholderName = '';
        this.payingDeposit = false;
      }
    },

    timeExpired(newTimeExpired) {
      if (newTimeExpired) {
        // disable buttons
        this.cardButtonDisable = true;
        this.$emit('payment-time-expired')
      } else {
        // enable buttons
        this.cardButtonDisable = false;
      }
    },

    cardPass() {
      this.cardError = null;
    },
  },

  methods: {
    /* ===============
     * public methods
     */

    takeSalePayment(sale, allowDepositFlag = false) {
      console.log('takeSalePayment', sale, allowDepositFlag)
      this.sale = sale;
      this.payingDeposit = allowDepositFlag;

      this.showTestingModal();
    },

    takeBookingPayment(booking) {
      // console.log(booking);
      this.sale = booking.sale
      this.saleable = booking;

      this.showTestingModal();

      // start expire timer
    },

    takeVoucherPayment(voucher) {
      this.sale = voucher.sale
      this.saleable = voucher;

      this.showTestingModal();
    },

    takeVirtualGamePayment(virtualGame, sale, from) {
      this.sale = sale;
      this.saleable = virtualGame;

      this.showTestingModal();
    },

    /*
     * END public methods
     * =============== */

    /* ===============
     * testingModal methods
     */

    showTestingModal() {
      // Do any prep before showing the modal
      this.cardholderName = this.sale.customer.name;

      $(this.$refs.testingModal).modal('handleUpdate');
      $(this.$refs.testingModal).modal('show');
    },

    hideTestingModal() {
      $(this.$refs.testingModal).modal('hide');
    },

    testingModalShown() {
      // console.log('testingModalShown');
    },

    testingModalHidden() {
      // console.log('testingModalHidden');
      this.sale = null;
    },

    /*
     * END testingModal methods
     * =============== */

    createIntent() {
      if (this.sale == null) {
        return;
      }

      let intent = {
        locationId: this.location.id,
        saleId: this.sale.id,
        payDeposit: this.payingDeposit,
      };

      this.loading(true, this.$refs.testingModal);

      axios.post(this.route('api.pay-testing.make-intent'), intent)
        .then((response) => {
          if (response.status == '201') { // HTTP_CREATED
            // pull out pi and client secret
            //
            this.intentId = response.data.intentId;
            this.paymentExpiry = response.data.paymentExpiry;
            // enable buttons
            this.cardButtonDisable = false;
          } else {
            flash('Error setting up payment', 'danger');
            console.log('createIntent', response.data);
            console.log('createIntent', response.status);
            console.log('createIntent', response.statusText);
          }
          this.loading(false);
        })
        .catch((error) => {
          flash('Error setting up payment', 'danger');
          if (error.response) {
            // if HTTP_UNPROCESSABLE_ENTITY some validation error laravel or us
            // else if HTTP_CONFLICT
            // else if HTTP_FORBIDDEN on enough permissions
            console.log('createIntent: Response error', error.response.data, error.response.status, error.response.headers);
          } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            console.error('createIntent: Request error', error.request);
          } else {
            // Something happened in setting up the request that triggered an Error
            console.error('createIntent: Error', error.message);
          }

          // this.loading(false);
        });
    },

    updateName() {
      this.nameError = null;
    },

    submitPayment() {
      // card input box
      if (! this.cardholderName) {
        this.nameError = 'Card holder name is required.';
        return;
      }

      this.loading(true, this.$refs.testingModal);
      this.cardButtonDisable = true;
      this.cardError = null;

      if (this.cardPass == false) {
        // Display error.message in your UI.
        flash('Payment error: Set to Fail', 'warning');
        this.cardError = 'Card set to Fail';
        this.cardButtonDisable = false;
        this.loading(false);
      } else {
        // The payment has succeeded. Display a success message.
        this.paymentSuccess();
      }
    },

    paymentSuccess() {
      // console.log('paymentSuccess');

      // let backend know?
      let intent = {
        locationId: this.location.id,
        saleId: this.sale.id,
        intentId: this.intentId,
      };

      this.loading(true, this.$refs.testingModal);

      axios.post(this.route('api.pay-testing.intent-success'), intent)
        .then((response) => {
          if (response.status == '202') { // HTTP_ACCEPTED
            console.log('paymentSuccess: accepted');
          } else if (response.status == '204') { // HTTP_NO_CONTENT
            console.log('paymentSuccess: confirmed');
          } else {
            flash('Error reporting payment success', 'danger');
            console.log('paymentSuccess', response.data);
            console.log('paymentSuccess', response.status);
            console.log('paymentSuccess', response.statusText);
          }

          this.loading(false);
          this.paymentSuccessConfirmed();
        })
        .catch((error) => {
          flash('Error reporting payment success', 'danger');
          if (error.response) {
            // if HTTP_UNPROCESSABLE_ENTITY some validation error laravel or us
            // else if HTTP_CONFLICT
            // else if HTTP_FORBIDDEN on enough permissions
            console.log('paymentSuccess: Response error', error.response.data, error.response.status, error.response.headers);
          } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            console.error('paymentSuccess: Request error', error.request);
          } else {
            // Something happened in setting up the request that triggered an Error
            console.error('paymentSuccess: Error', error.message);
          }

          this.loading(false);
          this.paymentSuccessError();
        });
    },

    paymentSuccessConfirmed() {
      // refresh page
      // sale gets nulled, so need to send this to the reload page timer
      this.reloadPageTimer(Vue.util.extend({}, JSON.parse(JSON.stringify(this.sale))));

      // swap modal
      this.hideTestingModal();
      $(this.$refs.successModal).modal('show');
    },

    paymentSuccessError() {
      // swap modal
      // this.hideTestingModal();
      // $(this.$refs.errorModal).modal('show');
    },

    reloadPageTimer(sale) {
      if (this.reloadTime <= 0) {
        window.location.href = this.route('location.payment.success', [this.location, sale]); // redirect to .... payment success page
      } else {
        setTimeout(() => {
          this.reloadTime -= 1;
          this.reloadPageTimer(sale);
        }, 1000);
      }
    },
  },

  mounted() {
    this.$nextTick(function () {
      $(this.$refs.vueRemove).contents().unwrap();
    });

    $(this.$refs.testingModal).modal('handleUpdate');
    $(this.$refs.testingModal).on('hidden.bs.modal', this.testingModalHidden);
    $(this.$refs.testingModal).on('shown.bs.modal', this.testingModalShown);
    $(this.$refs.successModal).modal('handleUpdate');

    this.currentTimeInterval = setInterval(() => {
      this.currentTime = moment(); // TODO: make as server timezone / same zone as paymentExpiry
    }, 100);
  },

  beforeDestroy() {
    clearInterval(this.currentTimeInterval);
  },
};
</script>


<style lang="scss" scoped>
/*
 * PayTesting.vue
 */

.custom-switch.custom-switch-adaptive {
  padding-left: 0;

  .custom-control-label {
    padding: 0 1.5em;
    position: relative;
    border-radius: 1em;
    line-height: 1.4em;
    color: var(--gray);
    border: 1px solid var(--gray);
    background-color: var(--light);
    transition: background-color 200ms;

    &::before {
      content: none;
    }
    &::after {
      position: absolute;
      height: 1em;
      top: 0.2em;
      left: 0.2em;
      width: 1em;
      border-radius: 1em;

      transition: left 200ms;
    }
  }

  .custom-control-input:checked ~ .custom-control-label {
    color: var(--white);
    background-color: var(--success);
    border-color: var(--success);

    &::after {
      background-color: var(--white);
      left: calc(100% - 1.2em);

      transform: none;
      -webkit-transform: none;
    }
  }
}

</style>
